import { useEffect, useState } from 'react'

/**
 * Whether the sections on the current tab may skip their anti-flash delay
 * because the boot screen already painted placeholders for them (RP-5936).
 *
 * The boot screen paints one tab, once. A section that takes over from those
 * placeholders should render its own skeleton immediately, since delaying it
 * would blank a column the viewer is already looking at. Anywhere else the
 * normal contract applies: hold back 150ms so a fast response never flashes a
 * skeleton.
 *
 * "Anywhere else" includes coming *back* to the painted tab — by then the
 * placeholders are long gone, so there is nothing to continue from. Hence the
 * one-way latch rather than a plain equality check.
 *
 * @param bootPaintedTab the tab whose placeholders the boot screen painted, or
 *   `null`/`undefined` when it painted none.
 * @param activeTab the tab currently showing.
 */
export function useBootSkeletonSkip(
  bootPaintedTab: string | null | undefined,
  activeTab: string,
): boolean {
  const [hasLeftBootTab, setHasLeftBootTab] = useState(false)

  useEffect(() => {
    if (bootPaintedTab && activeTab !== bootPaintedTab) {
      setHasLeftBootTab(true)
    }
  }, [bootPaintedTab, activeTab])

  return (
    !hasLeftBootTab &&
    bootPaintedTab !== null &&
    bootPaintedTab !== undefined &&
    activeTab === bootPaintedTab
  )
}
